import { PARAMETER_EMBEDDING_TARGET_NAME } from 'cadenza/api-client/workbook-embedding-targets-api';
import { on, unByKey } from 'cadenza/utils/event-util';
import type { EventKey } from 'cadenza/utils/event-util';
import { cadenzaLocalStorage } from 'cadenza/utils/storage/storage';
import { extendUrlParameters, getUrlParameters } from 'cadenza/utils/url-param-utils';

let skipConfirmation = false;
let eventKey: EventKey | undefined;

/**
 * Activate or deactivate the 'onbeforeunload' listener on the current shown page.
 * If confirm is enabled every navigation event is intercepted while the current session is
 * still active and a default browser confirmation dialog is shown to ensure navigation is really
 * wanted.
 *
 * @param confirm - Enable or disable 'onbeforeunload' listener on the current shown page
 */
export function navigateOnlyAfterConfirmation (confirm: boolean) {
  // Allow to disable the confirmation in development mode.
  if (window.Disy.debug && cadenzaLocalStorage.getItem('confirmNavigation') === 'false') {
    return;
  }

  if (confirm && !eventKey) {
    eventKey = on(window, 'beforeunload', confirmNavigationIfNeeded);
  } else if (!confirm && eventKey) {
    unByKey(eventKey);
    eventKey = undefined;
  }
}

/**
 * Returns `true` if the user has to confirm leaving the current page using the
 * browser confirm dialog.
 */
export function isNavigateOnlyAfterConfirmation () {
  return eventKey !== undefined;
}

/**
 * If the current shown page is in the confirmation mode you can use this method here to
 * navigate to a url without confirming the navigation
 *
 * @param url - URL to navigate to without showing a browser default confirmation dialog
 */
export function navigateWithoutConfirmation (url: string) {
  skipConfirmation = true;
  location.href = url;
}

export function navigateWithinWorkbook (url: string) {
  const embeddingTargetParams = getAllEmbeddingRelatedUrlParameters();
  if (Object.keys(embeddingTargetParams).length) {
    url = extendUrlParameters(url, embeddingTargetParams);
  }
  return navigateWithoutConfirmation(url);
}

export function getAllEmbeddingRelatedUrlParameters () {
  const urlParameters = getUrlParameters(location.href);
  const embeddingTargetId = urlParameters[PARAMETER_EMBEDDING_TARGET_NAME];
  let embeddingTargetIdParams: Record<string, unknown | unknown[]> = {};
  if (embeddingTargetId) {
    embeddingTargetIdParams = { [PARAMETER_EMBEDDING_TARGET_NAME]: embeddingTargetId };
    Object.keys(urlParameters).filter(key => key.includes('filter.'))
      .forEach(key => (embeddingTargetIdParams[key] = urlParameters[key]));
  }
  return embeddingTargetIdParams;
}

/**
 * According to https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
 * Only show discard changes browser default window...
 * ...if there's still a valid session which is the requirement for the user to save things in Cadenza Web.
 * ...if it was not disabled with the {@link navigateWithoutConfirmation} method before.
 * If a session timeout occurred, the 'Disy.clientSessionId' will be set to null and the
 * redirect to the session timeout page can happen without interrupting it.
 *
 * @param event - The 'onbeforeunload' event
 */
function confirmNavigationIfNeeded (event: BeforeUnloadEvent) {
  const hasActiveSession = Boolean(window.Disy.clientSessionId);
  if (hasActiveSession && !skipConfirmation) {
    event.preventDefault();
    event.returnValue = '';
  } else {
    delete event.returnValue;
  }
}

/**
 * Execute the given function when leaving the page.
 * This will NOT display a confirmation dialog to the user.
 *
 * @param callback - The function to be called
 * @return A unique key that can be used to unregister the listener using the un() function
 */
export function executeBeforeLeavingPage (callback: () => unknown) {
  return on(window, 'beforeunload', () => {
    callback();
  });
}
